import { faker } from '@faker-js/faker';
import { Direction, EventType, MatrixClient, MatrixEvent, Room } from 'matrix-js-sdk/src/matrix';

import { BreakoutRoomsSettingsFormData } from 'components/BreakoutRoomsSettingsSidebar/BreakoutRoomsSettingsForm';
import logger from 'utils/logging/faro';
import { getFilter, SPACE_CHILD_FILTER } from 'utils/matrixUtils';
import { getRoomStateEvents } from 'utils/roomHierarchyUtils';

import { RoomType as ChildRoomType } from './useRoomType';

export const BREAKOUT_LOG_PREFIX = '[Breakout Rooms]';

export enum RoomAssignType {
  MANUAL = 'manual',
  AUTO = 'auto',
  SELF_ASSIGN = 'self-assign',
}

export type BreakoutRoom = {
  id: string;
  title: string;
  userIds?: string[];
};

export type BreakoutRoomsFormData = {
  roomCount: number;
  assignment: RoomAssignType;
} & BreakoutRoomsSettingsFormData;

export const defaultBreakoutRoomFormValues = {
  roomCount: 1,
  assignment: RoomAssignType.SELF_ASSIGN,
  allowReturnToMain: true,
  displayTimeLeft: true,
  isTimeLimitActive: false,
  timeLimitHours: 0,
  timeLimitMinutes: 10,
  timeLimit: undefined,
};

export type FakeMember = {
  name: string;
  userId: string;
};

export const allMainRoomParticipants = Array.from({ length: 5 }, () => ({
  name: faker.name.firstName(),
  userId: faker.string.uuid(),
}));

export const getUnassignedParticipants = (rooms: BreakoutRoom[], allParticipants: FakeMember[]) => {
  const assignedUserIds = rooms.flatMap((room) => room.userIds);

  return allParticipants.filter((participant) => !assignedUserIds.includes(participant.userId));
};

/**
 * Get the space child events for a room, promise never rejects
 * @param matrixClient - The matrix client
 * @param room - The room
 * @returns The space child events
 */
export const getSpaceChildEvents = async (matrixClient: MatrixClient, room?: Room | null) => {
  const emptySpaceChildEvents = { breakoutRoomChildEvents: [], chatChildEvents: [], childEvents: [] };
  if (!room) return emptySpaceChildEvents;

  const spaceChildEvents = getRoomStateEvents(room, EventType.SpaceChild);

  // Room should have at least one space child event (chat), if we have no events we fetch them from the server
  if (!spaceChildEvents.length) {
    const filter = getFilter(SPACE_CHILD_FILTER, matrixClient.getUserId());
    const data = await matrixClient
      .createMessagesRequest(room.roomId, null, 20, Direction.Forward, filter)
      .catch((e) => logger.error('Failed to fetch space child events', e));

    if (!data) return emptySpaceChildEvents;

    const childEvents = data.chunk.map((event) => new MatrixEvent(event));
    spaceChildEvents.push(...childEvents);
  }

  return spaceChildEvents.reduce(
    (acc, evt) => {
      if (evt.isRedacted()) return acc;

      const type = evt.getContent()?.roomType;
      if (type === ChildRoomType.BREAKOUT) {
        acc.breakoutRoomChildEvents.push(evt);
      } else if (type === ChildRoomType.CHAT) {
        acc.chatChildEvents.push(evt);
      } else {
        acc.childEvents.push(evt);
      }
      return acc;
    },
    { breakoutRoomChildEvents: [], chatChildEvents: [], childEvents: [] } as {
      breakoutRoomChildEvents: MatrixEvent[];
      chatChildEvents: MatrixEvent[];
      childEvents: MatrixEvent[];
    }
  );
};
